import { apiRequest, ApiResponse } from '../request'
import { downloadBlob } from '../../download-blob'
import { getStoredToken } from '@/lib/api/session-refresh'

export type ReportType =
    | 'consultations'
    | 'orders'
    | 'exams'
    | 'admissions'
    | 'orders'
    | 'chats'
    | 'billings'
    | 'leads'
    | 'deals'
    | 'marketing-funnel'
    | 'clients'

export type BillingKind = 'RECEIVABLE' | 'PAYABLE'

export interface ReportFilters {
    startDate?: string
    endDate?: string
    /**
     * Aceita vários status (OR). Serializado como "PENDING,OVERDUE" por `buildParams`,
     * mesmo formato de `catalogIds`. String única continua válida — é o que vem de
     * automação salva e de link antigo.
     */
    status?: string[] | string
    professionalId?: number
    clientId?: number
    partnerId?: number
    isReturn?: boolean
    /** Somente cirurgias (procedimentos): filtra por `Catalog.isSurgical` do item. */
    isSurgical?: boolean
    /** Oculta a venda perdida (consultas, exames e procedimentos). Ver `lostSaleWhere`. */
    excludeLostSales?: boolean
    type?: string
    departmentId?: number
    attendantId?: number
    unitId?: number
    /** Aceita vários (OR). */
    paymentStatus?: string[] | string
    /** Aceita vários (OR). */
    paymentMethod?: string[] | string
    /**
     * Status da cobrança vinculada; `NONE` = sem cobrança. Aceita vários (OR) —
     * `NONE` junto com outros status soma "sem cobrança" ao conjunto.
     */
    billingStatus?: string[] | string
    /** Meio de pagamento da cobrança vinculada. Aceita vários (OR). */
    billingPaymentMethod?: string[] | string
    page?: number
    pageSize?: number
    sortBy?: 'dueDate' | 'amount' | 'createdAt' | 'paidAt'
    sortDir?: 'asc' | 'desc'
    companyId?: number
    organizationId?: number
    scope?: 'company' | 'organization'
    hospitalId?: number
    supplyId?: number
    /** Contrapartes de lançamento: fornecedor, usuário do sistema e colaborador de RH. */
    supplierId?: number
    userId?: number
    employeeId?: number
    /** @deprecated Use `catalogIds`. Mantido para automações salvas. */
    catalogId?: number
    /** Itens do catálogo (OR). Serializado como "1,2,3" por `buildParams`. */
    catalogIds?: number[]
    gender?: string
    minAge?: number
    maxAge?: number
    /** Usuário que criou o registro (consultas/exames/procedimentos). */
    createdById?: number
    /**
     * Qual data conta o período. Padrão em todos os tipos: `created` (quando o registro foi
     * lançado). Conforme o tipo: `scheduled` (data do atendimento), `due` (vencimento),
     * `admission` (data de internação), `paid` (data do pagamento da cobrança) e `approved`
     * (data da aprovação do orçamento — procedimentos e exames). Em `paid` só entram
     * registros com cobrança PAGA — é o regime de caixa; em `approved`, só os orçamentos
     * aprovados.
     */
    dateBasis?: 'created' | 'scheduled' | 'due' | 'admission' | 'paid' | 'approved' | 'sold'
    /**
     * Colunas do export, por chave, separadas por vírgula. É a seleção do menu
     * "Colunas" da tela; ausente = todas. Não filtra dados, só o que sai no
     * arquivo — por isso não entra na busca da listagem.
     */
    columns?: string
    /**
     * Soma por item de catálogo em vez de listar atendimento a atendimento.
     * Muda a FORMA da resposta — use `getGroupedReport`, não `getReportDataPaged`.
     */
    grouped?: string
    [key: string]: any
}

/** Uma linha do modo agrupado: um item de catálogo, somado no período. */
export interface GroupedReportRow {
    catalogId: number | null
    name: string
    /** Soma das quantidades (25 hemogramas, ainda que em 20 atendimentos). */
    count: number
    appointments: number
    grossSale: number
    /**
     * Menor e maior preço unitário PRATICADOS no período — exatos, tirados das
     * vendas. Iguais = preço único (a tela mostra um valor só); diferentes = faixa.
     *
     * Não existe um unitário único no grupo: o preço é congelado em cada venda e muda
     * com reprecificação, variação e preço próprio do parceiro. Uma média não
     * corresponderia a venda nenhuma.
     */
    unitPriceMin: number | null
    unitPriceMax: number | null
    discountAmount: number
    netValue: number
    cost: number
    profit: number
    /** Atendimentos sem custo apurado — ficam fora de custo e lucro. */
    costUnknownCount: number
}

export interface GroupedReportResult {
    items: GroupedReportRow[]
    /** Total de itens distintos. */
    total: number
    truncated: boolean
}

/** Tipos que somam por item de catálogo. Espelha `GROUPABLE_TYPES` do backend. */
export const GROUPABLE_REPORT_TYPES: ReportType[] = [
    'consultations',
    'exams',
    'orders',
]

export interface ReportStats {
    total: number
    byStatus: Record<string, number>
    byPeriod: { date: string; count: number }[]
    totalRevenue?: number
    averageTicket?: number
    /** Linhas que geraram receita — é o divisor do `averageTicket`. */
    revenueItemCount?: number
    revenueByPeriod?: { date: string; revenue: number }[]
    totalReceivable?: number
    totalPayable?: number
    netBalance?: number
    /** Custo/lucro apurados. Somam apenas as linhas com snapshot de repasse. */
    totalCost?: number
    totalProfit?: number
    /** Margem (%) sobre a receita líquida das linhas apuradas. `null` se não apurável. */
    profitMargin?: number | null
    costKnownCount?: number
    /** Linhas sem custo apurado — ficam fora de `totalCost`/`totalProfit`. */
    costUnknownCount?: number
    averageAge?: number
    withBirthDate?: number
    totalSpend?: number
    averageSpend?: number
}

export interface ReportOverview {
    totalAttendances: number
    totalRevenue: number
    averageTicket: number
    revenueGrowth: number
    attendancesGrowth: number
    byType: Record<string, { total: number; revenue: number }>
}

export interface PagedResult<T> {
    items: T[]
    total: number
    page: number
    pageSize: number
    pageCount: number
}

export const FINANCIAL_REPORT_TYPES: ReportType[] = [
    'consultations',
    'exams',
    'orders',
    'orders',
    'billings',
    'deals',
    'marketing-funnel',
]

function buildParams(filters: ReportFilters): Record<string, string> {
    const params: Record<string, string> = {}
    Object.entries(filters).forEach(([key, value]) => {
        // Lista vazia é "sem filtro", não "nenhum resultado": `String([])` daria `""`,
        // que o backend leria como ausente — mas deixar passar geraria um par
        // `status=` inútil na URL.
        if (Array.isArray(value) && value.length === 0) return
        if (value !== undefined && value !== null && value !== '') {
            // `String(['PENDING','OVERDUE'])` já produz "PENDING,OVERDUE", o formato
            // que o `parseStringList` do backend espera.
            params[key] = String(value)
        }
    })
    return params
}

export async function getReportOverview(
    filters: ReportFilters
): Promise<ApiResponse<ReportOverview>> {
    return apiRequest<ReportOverview>('reports/overview', {
        params: buildParams(filters),
    })
}

export async function getReportData(
    type: ReportType,
    filters: ReportFilters
): Promise<ApiResponse<any[]>> {
    return apiRequest<any[]>(`reports/${type}`, {
        params: buildParams(filters),
    })
}

export async function getReportDataPaged(
    type: ReportType,
    filters: ReportFilters
): Promise<ApiResponse<PagedResult<any>>> {
    return apiRequest<PagedResult<any>>(`reports/${type}/paged`, {
        params: buildParams(filters),
    })
}

/** O relatório somado por item de catálogo (uma linha por item). */
export async function getGroupedReport(
    type: ReportType,
    filters: ReportFilters
): Promise<ApiResponse<GroupedReportResult>> {
    return apiRequest<GroupedReportResult>(`reports/${type}/grouped`, {
        params: buildParams(filters),
    })
}

export async function getReportStats(
    type: ReportType,
    filters: ReportFilters
): Promise<ApiResponse<ReportStats>> {
    return apiRequest<ReportStats>(`reports/${type}/stats`, {
        params: buildParams(filters),
    })
}

export async function exportReport(
    type: ReportType,
    format: 'pdf' | 'xlsx' | 'csv',
    filters: ReportFilters
): Promise<void> {
    const params = new URLSearchParams(buildParams(filters))
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/reports/${type}/export/${format}?${params.toString()}`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar relatório')

    const blob = await response.blob()
    const extension = format === 'xlsx' ? 'xlsx' : format
    const filename = `relatorio-${type}-${Date.now()}.${extension}`

    downloadBlob(blob, filename)
}

export async function sendReportByEmail(
    type: ReportType,
    email: string,
    format: string,
    filters: ReportFilters
): Promise<ApiResponse<any>> {
    return apiRequest<any>(
        `reports/${type}/send-email`,
        {
            data: { email, format, ...filters },
        },
        'post'
    )
}

// ─── Comissão por produtividade ──────────────────────────────────────────────

/** Como a taxa é lida: valor por atendimento ou percentual sobre a receita. */
export type CommissionMode = 'fixed' | 'percentage'

/**
 * Um tipo de atendimento na conta.
 *
 * Os filtros vão **por tipo**, já resolvidos pela tela: o vocabulário de situação
 * diverge entre consulta, exame e procedimento, e é a tela que sabe o que o operador
 * marcou no filtro comum.
 */
export interface CommissionEntry {
    type: 'consultations' | 'exams' | 'orders'
    filters: ReportFilters
}

export interface CommissionRequest {
    /**
     * De quem é a comissão. **A taxa não vai no corpo** — o servidor a lê do cadastro
     * do usuário. Era digitada aqui a cada apuração, sem ficar registrada em lugar
     * nenhum; ver `getCommissionSettings`.
     */
    userId: number
    entries: CommissionEntry[]
    recipientName?: string
    periodLabel?: string
}

/** Como um tipo é comissionado, já resolvido (o próprio, ou o herdado do geral). */
export interface ResolvedTypeCommission {
    mode: CommissionMode
    /** Centavos por atendimento (`fixed`) ou basis points (`percentage`). */
    rate: number
    /** Usa o critério GERAL, e não um próprio. */
    inheritsMode: boolean
}

/** A regra cadastrada do usuário, já resolvida por tipo de atendimento. */
export interface CommissionSettings {
    /** Critério geral — o dos tipos que não têm o seu. */
    mode: CommissionMode
    /**
     * Base do percentual na empresa da sessão (Configurações Financeiras >
     * Precificação). Numa apuração da organização, cada atendimento usa a da sua
     * empresa — o resultado do cálculo traz a base efetiva por linha.
     */
    basis: 'REVENUE' | 'MARGIN'
    /** Taxa geral. `null` quando só há taxas por tipo. */
    rate: number | null
    byType: {
        consultations: ResolvedTypeCommission
        exams: ResolvedTypeCommission
        orders: ResolvedTypeCommission
    }
    updatedBy: { id: number; name: string } | null
    updatedAt: string | null
}

/**
 * A comissão cadastrada do usuário. Responde erro quando ele não tem regra — é o que
 * impede apurar comissão sem nada combinado.
 */
export async function getCommissionSettings(
    userId: number
): Promise<ApiResponse<CommissionSettings>> {
    return apiRequest<CommissionSettings>('reports/commission/settings', {
        params: { userId },
    })
}

export interface CommissionResultLine {
    type: string
    label: string
    /** Critério desta linha — o tipo pode ter o seu, diferente do geral. */
    mode: CommissionMode
    rate: number
    /**
     * Sobre o que o percentual incidiu: venda (`REVENUE`) ou markup (`MARGIN`),
     * conforme a configuração financeira da empresa de cada atendimento. `mixed`
     * quando a apuração junta unidades com bases diferentes.
     */
    basis: 'REVENUE' | 'MARGIN' | 'mixed'
    count: number
    revenueCents: number
    /** Soma do que o percentual multiplicou (receita ou markup). */
    baseCents: number
    /** Atendimentos em base de markup sem repasse apurado — ficaram sem comissão. */
    costUnknownCount: number
    commissionCents: number
    /** O período tem mais atendimentos do que o teto de leitura conseguiu somar. */
    truncated: boolean
}

export interface CommissionResult {
    mode: CommissionMode
    lines: CommissionResultLine[]
    totalCount: number
    totalRevenueCents: number
    totalBaseCents: number
    totalCommissionCents: number
    costUnknownCount: number
    truncated: boolean
}

/**
 * Calcula a comissão no servidor — **atendimento a atendimento**, somando.
 *
 * O cálculo é o mesmo que alimenta o demonstrativo em PDF; fazê-lo na tela por cima de
 * agregados daria um total que a soma das linhas do documento não fecharia.
 */
export async function previewCommission(
    payload: CommissionRequest
): Promise<ApiResponse<CommissionResult>> {
    return apiRequest<CommissionResult>(
        'reports/commission/preview',
        { data: payload },
        'post'
    )
}

/** Baixa o demonstrativo de comissão em PDF. */
export async function downloadCommissionStatement(
    payload: CommissionRequest
): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const response = await fetch(`${baseUrl}/reports/commission/statement`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    })

    if (!response.ok) throw new Error('Erro ao gerar o demonstrativo')

    downloadBlob(
        await response.blob(),
        `demonstrativo-comissao-${Date.now()}.pdf`
    )
}
